// @ts-check
const React = require('react');
const bytes = require('bytes');
const { ExportToCsv } = require('export-to-csv');
const { isNumber } = require('lodash');
const moment = require('moment');
const randomcolor = require('randomcolor');
const {
  DISCO_DEFAULT_TABLE_PAGE_SIZE,
  MS_IN_DAY,
  MS_IN_MINUTE,
  MS_IN_HOUR,
  LAST_DAYS_FASE_ONLINE,
  NR_RECENT_FLIGHTS,
  DEFAULT_FIRMWARE_TYPE,
  DEFAULT_MIN_FREE_DISK_SPACE_GB,
  LAST_MINUTES_ONLINE,
  ROLES_MAP,
  CLOCK_SKEW_THRESHOLD,
  MAX_ERROR_MESSAGE_LENGTH,
  KNOWN_TYPES,
} = require('./constants');

function sortObjectKeys(obj = {}) {
  return Object.keys(obj)
    .sort()
    .reduce((acc, key) => ({ ...acc, [key]: obj[key] }), {});
}

function humanizeFileSize(size) {
  if (!size || size < 1) {
    return '0';
  }
  const i = Math.floor(Math.log(size) / Math.log(1000));
  // Number(...) strips trailing zeros ("1.50" -> 1.5, "1.00" -> 1), matching
  // the old `* 1` coercion but without multiplying a string.
  return `${Number((size / 1000 ** i).toFixed(2))} ${['B', 'kB', 'MB', 'GB', 'TB'][i]}`;
}

function dehumanizeFileSize(size) {
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const foundUnit = units.find((u) => size.indexOf(u) > -1 && !isNaN(size[size.length - u.length - 1]));
  // No recognisable unit (e.g. empty/malformed input) -> treat as 0 bytes
  // rather than crashing on `foundUnit.length`.
  if (!foundUnit) {
    return 0;
  }
  const amount = Number.parseFloat(size.slice(0, -foundUnit.length));
  const amountInBytes = amount * 1000 ** units.indexOf(foundUnit);
  return amountInBytes;
}

function stripArrays(obj) {
  if (Array.isArray(obj) && obj.length > 0) {
    const type = typeof obj[0];
    return `Array of ${obj.length} ${type} items`;
  }
  if (Array.isArray(obj)) {
    return `Array of ${obj.length} items`;
  }
  if (typeof obj === 'object' && obj !== null) {
    const newObj = {};
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        newObj[key] = stripArrays(obj[key]);
      }
    }
    return newObj;
  }
  return obj;
}

function humanizeDownloadSpeed(speedBytesPerSec) {
  const hunanizedBytes = humanizeFileSize(speedBytesPerSec);
  return `${hunanizedBytes}/s`;
}

/**
 * Base64-encode JSON for URL filter/sort params.
 * Uses UTF-8 so pasted Unicode (ZWSP, smart quotes, etc.) does not throw like `btoa(json)`.
 * ASCII-only payloads match classic `btoa(JSON.stringify(...))` byte-for-byte.
 */
function encodeBase64Json(value) {
  const json = JSON.stringify(value);
  if (typeof Buffer !== 'undefined') {
    return Buffer.from(json, 'utf8').toString('base64');
  }
  const bytes = new TextEncoder().encode(json);
  let binary = '';
  for (let i = 0; i < bytes.length; i += 1) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

/**
 * Decode base64 JSON from encodeBase64Json.
 * Tries strict UTF-8 first, then Latin-1 for legacy `btoa` bookmarks (U+0080–U+00FF).
 */
function decodeBase64Json(encoded) {
  if (encoded == null || encoded === '') {
    throw new Error('Missing base64 JSON payload');
  }
  const bytes =
    typeof Buffer !== 'undefined'
      ? new Uint8Array(Buffer.from(String(encoded), 'base64'))
      : Uint8Array.from(atob(String(encoded)), (c) => c.charCodeAt(0));
  try {
    return JSON.parse(new TextDecoder('utf-8', { fatal: true }).decode(bytes));
  } catch {
    return JSON.parse(new TextDecoder('latin1').decode(bytes));
  }
}

function getQueryUrl(filters = []) {
  const filtersb64 = encodeBase64Json(filters);
  const sortsb64 = encodeBase64Json([]);
  const params = {
    pageSize: DISCO_DEFAULT_TABLE_PAGE_SIZE,
    page: 0,
    sorts: sortsb64,
    filters: filtersb64,
    hideOffline: true,
    onlyProduction: true,
  };
  const queryString = Object.keys(params)
    .map((key) => `${key}=${params[key]}`)
    .join('&');
  return `#devices/?${queryString}`;
}

function getAirfiCommandFromObject(config = {}, prefix = '') {
  return Object.keys(config || {}).reduce((acc, cur) => {
    acc += `--put ${cur} ${config[cur]} `;
    return acc;
  }, prefix);
}

// convert version and type into firmware filename
function getFirmwareFilename(version, type) {
  return `${version}-${type}`;
}

// get firmware version and type from firmware filename
function parseFirmwareFilename(filename) {
  const matchedType = KNOWN_TYPES.find((type) => filename.endsWith(`-${type}`));

  if (matchedType) {
    const version = filename.slice(0, filename.length - matchedType.length - 1);
    return { version, type: matchedType };
  }

  return { version: filename, type: DEFAULT_FIRMWARE_TYPE };
}

const isFaseFirmwareOk = (device) =>
  device.currentFirmware &&
  device.assignedFirmware &&
  device.currentFirmware.version === device.assignedFirmware.version &&
  device.currentFirmware.type === device.assignedFirmware.type;
const isFreeDiskSpaceOk = (freeDiskSpaceInBytes, minWantedFreeGigabytes = DEFAULT_MIN_FREE_DISK_SPACE_GB) => {
  const currentFreeSpace = parseInt(freeDiskSpaceInBytes);
  if (!isNumber(currentFreeSpace) || currentFreeSpace === 0) {
    // boxes that dont support this send '0'
    return true;
  }
  return currentFreeSpace >= bytes(`${minWantedFreeGigabytes}GB`);
};
const isFaseOnlineOk = (lastSeenTimestamp) => isWithinLastDays(lastSeenTimestamp, LAST_DAYS_FASE_ONLINE);
const isFaseContentOk = (pendingContentCount) => pendingContentCount === 0;
const isFaseFutureContentOk = (device) => device.contentSyncStatus !== 'future content download';
const isFaseRecentFlightsOk = (flights) => {
  const lastFlights = flights.filter((flight) => isWithinLastDays(flight.arrivalTimestamp, LAST_DAYS_FASE_ONLINE));
  return lastFlights.length >= NR_RECENT_FLIGHTS;
};

// Calculate if a given date is within given milliseconds.
function isWithinLastMilliseconds(dateISOString, ms) {
  const date = new Date(dateISOString);
  const recentDate = new Date(Date.now() - ms);

  return date >= recentDate;
}

// Calculate if a given date is within given minutes.
function isWithinLastMinutes(dateISOString, minutes) {
  const ms = minutes * MS_IN_MINUTE;
  return isWithinLastMilliseconds(dateISOString, ms);
}

// Calculate if a given date is within given minutes.
function isWithinLastHours(dateISOString, hours) {
  const ms = hours * MS_IN_HOUR;
  return isWithinLastMilliseconds(dateISOString, ms);
}

// Calculate if a given date is within given days.
function isWithinLastDays(dateISOString, days) {
  const ms = days * MS_IN_DAY;
  return isWithinLastMilliseconds(dateISOString, ms);
}

function buildQueryString(params, encode = false) {
  if (!params) {
    return '';
  }

  return Object.keys(params)
    .filter((k) => {
      // Exclude operationalStatuses / purposes / customerCodes when an explicit
      // 'serials' filter is present (the serial list takes precedence). NB: the
      // previous version used a comma operator, so only 'customerCodes' was
      // actually excluded — operationalStatuses/purposes leaked through.
      if (params.serials && (k === 'operationalStatuses' || k === 'purposes' || k === 'customerCodes')) {
        return false;
      }
      return params[k] !== undefined && params[k] !== null;
    })
    .map((key) => {
      const value = params[key];
      if (encode) {
        return `${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
      }
      return `${key}=${value}`;
    })
    .join('&');
}

function parseQueryString(queryString) {
  const params = {};

  if (!queryString) {
    return params;
  }

  // Remove the leading '?' if it exists
  const cleanQueryString = queryString.startsWith('?') ? queryString.slice(1) : queryString;

  const pairs = cleanQueryString.split('&');

  pairs.forEach((pair) => {
    const [key, value] = pair.split('=');
    params[decodeURIComponent(key)] = decodeURIComponent(value);
  });

  return params;
}

function groupBy(xs = [], key) {
  return xs.reduce((rv, x) => {
    let value;
    if (typeof key === 'string') {
      value = x[key];
    } else {
      value = key(x);
    }
    (rv[value] = rv[value] || []).push(x);
    return rv;
  }, {});
}

function humanizePercentage(number, normalized = true) {
  if (isNaN(number) || number === null || number === undefined) {
    return '';
  }
  if (normalized) {
    return `${Math.round(100 * number)}%`;
  }
  return `${Math.round(number)}%`;
}

function getDeviceByPosition(devices, position) {
  return devices && devices.find((d) => d.position && d.position.name === position);
}

function sortByPosition(a, b) {
  return (a.position && a.position.ordinal) - (b.position && b.position.ordinal);
}

function hasAllKeys(object, keys) {
  return keys.every((item) => object.hasOwnProperty(item));
}

function getNameFromS3Key(key) {
  return key.replace(/^.*[\\\/]/, '');
}

// convert IP address from ISO format to x.x.x.x
function parseISOSerial(serial) {
  if (serial.indexOf('-') > -1 && serial.split('-').length === 4) {
    return serial.replaceAll('-', '.');
  }
  return serial;
}

// stringify error object
function humanizeError(error) {
  let errorMessage = 'server error';

  if (typeof error === 'string') {
    errorMessage = error;
  } else if (error && error.message) {
    errorMessage = error.message;
  } else if (error && error.description) {
    errorMessage = error.description;
  } else if (error && error.error) {
    errorMessage = error.error;
  } else if (error && error.reason) {
    errorMessage = error.reason;
  }

  // Truncate long error messages
  if (errorMessage.length > MAX_ERROR_MESSAGE_LENGTH) {
    errorMessage = `${errorMessage.substring(0, MAX_ERROR_MESSAGE_LENGTH)}... [truncated]`;
  }

  return errorMessage;
}

function isDeviceOnline(lastSeenTimestamp) {
  return isWithinLastMinutes(lastSeenTimestamp, LAST_MINUTES_ONLINE);
}

function getReadyStatus(device) {
  const { contentSyncStatus } = device;
  const contentReady = contentSyncStatus === 'completed' || contentSyncStatus === 'ready';
  if (contentReady && (device.isSuperProxy || (device.batteryLevel && device.batteryLevel >= 99))) {
    return 'ready';
  }
  if (contentReady) {
    return 'charging';
  }
  if (contentSyncStatus === 'error') {
    return 'error';
  }
  return 'syncing';
}

function humanizeDurationInSecondsToHours(durationInSeconds) {
  if (Number.isNaN(durationInSeconds) || durationInSeconds === null || durationInSeconds === undefined) {
    return '';
  }

  const durationInMinutes = durationInSeconds / 60;
  const durationInHours = durationInSeconds / 3600;

  if (durationInHours >= 1) {
    const durationInHoursRounded = Math.round(durationInHours);
    return `${durationInHoursRounded}h`;
  }
  if (durationInMinutes >= 1) {
    const durationInMinutesRounded = Math.round(durationInMinutes);
    return `${durationInMinutesRounded}m`;
  }
  return `${durationInSeconds}s`;
}

function checkRole(entity, userRoles, fieldsToUpdate) {
  if (userRoles.includes('ROLE_FIRMWARE')) {
    return true;
  }
  const entityRoles = ROLES_MAP[entity];
  if (!entityRoles) {
    return false;
  }
  // Check each field if it can be edited by the user's role
  for (const field of fieldsToUpdate) {
    const allowedRoles = entityRoles[field];

    // If the field doesn't exist in the rolesMap or the user doesn't have the role for this field
    if (!allowedRoles || !userRoles.some((role) => allowedRoles.includes(role))) {
      return false;
    }
  }

  return true;
}

function getFilterLink(relativePath, filters = [], filterOptions = {}) {
  const filterBase64 = encodeBase64Json(filters);
  return `#${relativePath}?filters=${filterBase64}&${buildQueryString(filterOptions)}`;
}

function removeExtension(filename) {
  return filename.replace(/\.[^\.]+$/, '');
}

function getBoxName(nickname, serial) {
  return nickname ? `${nickname} - ${serial}` : serial;
}

/** Strip invisible/format paste junk; keep newlines/tabs used as separators. No NFKC (free-text safe). */
function sanitizeRawFilterInput(inputStr) {
  return String(inputStr || '')
    .replace(/\p{Cf}/gu, '') // ZWSP, BOM, soft hyphen, word joiner, …
    .replace(/[\u2010-\u2015\u2212]/g, '-') // typographic dashes → hyphen
    .replace(/[\u00A0\u202F\u2007]/g, ' '); // odd spaces → regular space
}

function extractValuesFromRawInput(inputStr) {
  return sanitizeRawFilterInput(inputStr)
    .split(/[\s,]+/)
    .filter((word) => word.trim() !== '');
}

/**
 * Parse pasted/typed serial list into canonical `10.0.x.x` values.
 * Rejects non-serial tokens (do not strip letters — that coerced nicknames into real serials).
 * A non-empty query must never become an empty list (would drop the filter and show the whole fleet).
 */
function extractSerialsFromRawInput(inputStr) {
  const raw = String(inputStr || '');
  // NFKC before dash canonicalization so compatibility dashes (e.g. U+FE58) become replaceable.
  const sanitized = sanitizeRawFilterInput(raw.normalize('NFKC'));
  const words = sanitized.split(/[\s,]+/).filter((word) => word.trim() !== '');
  const serials = words
    .filter((word) => /^[\d.-]+$/.test(word))
    .map((word) => {
      let value = word;
      if (value.length && value.split('.').length === 2) {
        value = `10.0.${word}`;
      } else if (value.length && value.split('-').length === 2) {
        value = `10-0-${word}`;
      }
      return parseISOSerial(value);
    });
  if (serials.length) return serials;
  if (words.length) return words;
  // Invisible-only paste (e.g. ZWSP) sanitizes to empty; keep raw so the filter is not dropped.
  return raw.trim() ? [raw] : [];
}

function expandSerialFiltersForQuery(filtered = []) {
  if (!Array.isArray(filtered) || !filtered.length) {
    return filtered;
  }
  const serialEntries = filtered.filter((f) => f && f.id === 'serial' && f.value);
  if (!serialEntries.length) {
    return filtered;
  }
  const otherFilters = filtered.filter((f) => !f || f.id !== 'serial');
  const serialValues = [
    ...new Set(serialEntries.reduce((acc, f) => acc.concat(extractSerialsFromRawInput(String(f.value))), [])),
  ];
  return [...otherFilters, ...serialValues.map((value) => ({ id: 'serial', value }))];
}

function predictableStringify(obj) {
  if (Array.isArray(obj)) {
    return `[${obj.map(predictableStringify).join(',')}]`;
  }
  if (typeof obj === 'object' && obj !== null) {
    const sortedKeys = Object.keys(obj).sort();
    return `{${sortedKeys.map((key) => `"${key}":${predictableStringify(obj[key])}`).join(',')}}`;
  }
  if (typeof obj === 'string') {
    return JSON.stringify(obj); // Handle strings that may contain special characters or whitespace
  }
  return obj;
}

function stripDisplayContent(inputString) {
  if (!inputString) {
    return inputString;
  }

  return inputString
    .replace(/Display\s*:\s*|\w+:\s*/gi, '')
    .replace(/\s*\/\s*\d+\s+SYNCING\s*$/i, '')
    .trim();
}

function filterOnEnter({ onChange }) {
  return React.createElement(
    'div',
    { className: 'disco-col-filter' },
    React.createElement('i', { className: 'ri-search-line disco-col-filter-icon', 'aria-hidden': true }),
    React.createElement('input', {
      className: 'disco-col-filter-input',
      placeholder: 'Filter…',
      onKeyPress(event) {
        if (event.keyCode === 13 || event.which === 13) {
          // event.target is EventTarget under @types/react; currentTarget is the <input>.
          onChange(/** @type {HTMLInputElement} */ (event.currentTarget).value.trim());
        }
      },
    }),
    React.createElement('span', { className: 'disco-col-filter-hint', title: 'Press Enter to apply' }, '↵'),
  );
}

function filterBoxEvents(events, filters) {
  return events.filter((event) => {
    for (const filter of filters) {
      // Check if the event has the property we want to filter by
      if (!event.hasOwnProperty(filter.id)) {
        continue;
      }

      // Filter by exact match
      if (typeof event[filter.id] === 'string' || typeof event[filter.id] === 'number') {
        if (String(event[filter.id]) !== String(filter.value)) {
          return false;
        }
      }

      // For array values, check if the filter value exists in the array
      else if (Array.isArray(event[filter.id])) {
        if (!event[filter.id].includes(filter.value)) {
          return false;
        }
      }

      // For object values, check if the filter value exists as one of the object's values
      else if (typeof event[filter.id] === 'object') {
        if (!Object.values(event[filter.id]).includes(filter.value)) {
          return false;
        }
      }
    }
    return true;
  });
}

function getHighlightedConfig(str = '') {
  const elements = [];
  const words = str.split(' ');

  let flag = null;
  let key = null;
  let value = '';

  words.forEach((word, index) => {
    const isLastWord = index === words.length - 1;
    if (word.startsWith('--')) {
      if (flag && key) {
        elements.push(
          React.createElement('span', { className: 'flag', key: `flag${index}` }, `${flag} `),
          React.createElement('span', { className: 'key', key: `key${index}` }, key + (value ? ' ' : '')),
          React.createElement('span', { className: 'value', key: `value${index}` }, value + (isLastWord ? '' : ' ')),
        );
      }
      flag = word;
      key = null;
      value = '';
    } else if (!flag) {
      elements.push(React.createElement('span', { key: `plain${index}` }, word + (isLastWord ? '' : ' ')));
    } else if (!key) {
      key = word;
    } else {
      value += value ? ` ${word}` : word;
    }
  });

  if (flag && key) {
    elements.push(
      React.createElement('span', { className: 'flag', key: 'flagLast' }, `${flag} `),
      React.createElement('span', { className: 'key', key: 'keyLast' }, key + (value ? ' ' : '')),
      React.createElement('span', { className: 'value', key: 'valueLast' }, value),
    );
  }

  return elements;
}

function isRetailMasterDb(dbName = '') {
  return typeof dbName === 'string' && dbName.startsWith('mi-master-');
}

/** DBs to search when installed content has no sourceDb (order matters). */
function getManifestDbCandidates(customerCode = '', isUat = false) {
  const iata = String(customerCode).toLowerCase();
  const contentDb = isUat ? `airfi-uat-${iata}` : `airfi-master-${iata}`;
  return ['master-config', contentDb, `mi-master-${iata}`];
}

/**
 * Allowlist box-reported sourceDb to this customer's candidate DBs.
 * UAT boxes may report box-local airfi-master-${iata}, which maps to airfi-uat-${iata}.
 * Returns the cloud DB name to use, or null if sourceDb must be ignored.
 */
function sanitizeManifestSourceDb(sourceDb = '', customerCode = '', isUat = false) {
  if (!sourceDb || typeof sourceDb !== 'string' || !customerCode) {
    return null;
  }
  const iata = String(customerCode).toLowerCase();
  const candidates = getManifestDbCandidates(customerCode, isUat);
  if (candidates.includes(sourceDb)) {
    return sourceDb;
  }
  // UAT boxes sync under airfi-master-* locally; only accept this customer's suffix.
  if (isUat && sourceDb === `airfi-master-${iata}`) {
    return `airfi-uat-${iata}`;
  }
  return null;
}

function getManifestAccountId(dbName = '', airfiConfig = {}) {
  const isStagingBox = airfiConfig && airfiConfig.ENVIRONMENT_MODE === 'staging';
  const customerCode = airfiConfig && airfiConfig.IATA;
  const envLabel = isStagingBox ? 'Staging' : 'Production';
  if (isRetailMasterDb(dbName) && customerCode) {
    return `${customerCode.toUpperCase()} ${envLabel}`;
  }
  return `AirFi ${envLabel}`;
}

/**
 * Map box-reported sourceDb to the CouchDB name DISCO should query/link.
 * Rejects foreign/unknown DBs (falls back to the purpose-matching content DB).
 */
function resolveManifestLinkDb(sourceDb = '', customerCode = '', isUat = false) {
  const iata = String(customerCode).toLowerCase();
  const defaultContentDb = isUat ? `airfi-uat-${iata}` : `airfi-master-${iata}`;
  if (!sourceDb) {
    return defaultContentDb;
  }
  return sanitizeManifestSourceDb(sourceDb, customerCode, isUat) || defaultContentDb;
}

function getManifestUrl(manifestId = '', airfiConfig = {}, sourceDb = '', isUat = false) {
  const customerCode = airfiConfig && airfiConfig.IATA;
  if (!customerCode) {
    return manifestId;
  }
  const dbName = resolveManifestLinkDb(sourceDb, customerCode, isUat);
  const accountId = getManifestAccountId(dbName, airfiConfig);
  return `#databaseDoc?accountId=${encodeURIComponent(accountId)}&dbName=${encodeURIComponent(
    dbName,
  )}&docId=${encodeURIComponent(manifestId)}`;
}

function exportCSVFile(items, name) {
  const title = `Disco-${name}-${new Date().toISOString()}`;
  const options = {
    fieldSeparator: ',',
    quoteStrings: '"',
    decimalSeparator: '.',
    title,
    filename: title,
    useTextFile: false,
    useBom: true,
    useKeysAsHeaders: true,
  };

  const csvExporter = new ExportToCsv(options);
  csvExporter.generateCsv(items);
}

// `regex` must be a RegExp (callers pass `new RegExp(...)`). The default is a
// never-matching pattern so a missing argument yields no highlighting instead
// of crashing on `''.test` (a string has no `.test` method).
function highlightSubstring(str = '', regex = /(?!)/) {
  return str.split(regex).map((part, index) => {
    if (regex.test(part)) {
      return React.createElement('strong', { key: index }, part);
    }
    return React.createElement('span', { key: index }, part);
  });
}

function isValidSegment(segment) {
  const num = parseInt(segment, 10);
  return num >= 0 && num <= 255;
}

function isExemptFromRoleCheck(openedEntityDbName, openedEntity) {
  if (openedEntityDbName === 'device') {
    return openedEntity && openedEntity.purpose && openedEntity.purpose.name === 'Development';
  }
  if (openedEntityDbName === 'firmware') {
    return openedEntity && openedEntity.label === 'internal';
  }
  return false;
}

function validateAndFormatSerial(rawInput) {
  // If it's already in IP format (x.x.x.x), validate it directly
  if (/^\d+\.\d+\.\d+\.\d+$/.test(rawInput)) {
    const segments = rawInput.split('.');
    if (segments.some((segment) => !isValidSegment(segment))) {
      return false;
    }
    return rawInput;
  }

  // Try to match 4-segment pattern first
  const fourSegmentMatch = rawInput.match(/^(\d+)([.-])(\d+)([.-])(\d+)([.-])(\d+)$/);
  if (fourSegmentMatch) {
    const segments = [fourSegmentMatch[1], fourSegmentMatch[3], fourSegmentMatch[5], fourSegmentMatch[7]];
    if (segments.some((segment) => !isValidSegment(segment))) {
      return false;
    }
    return segments.join('.');
  }

  // Try to match 2-segment pattern
  const twoSegmentMatch = rawInput.match(/^(\d+)([.-])(\d+)$/);
  if (twoSegmentMatch) {
    const segments = [twoSegmentMatch[1], twoSegmentMatch[3]];
    if (segments.some((segment) => !isValidSegment(segment))) {
      return false;
    }
    // Prepend '10.0.' for two segments
    return `10.0.${segments.join('.')}`;
  }

  return false;
}

function parseConfig(configString) {
  const args = configString.split('--');
  return args.reduce((configArray, arg) => {
    if (!arg) {
      return configArray;
    }
    const part = arg.trim();
    const command = part.split(' ')[0];
    const key = part.split(' ')[1];
    const value = part.split(' ')[2] || '';
    configArray.push({ command: `--${command}`, key, value });
    return configArray;
  }, []);
}

function getConfig(configString, key) {
  const config = parseConfig(configString);
  const item = config.find((c) => c.key === key);
  return item ? item.value : '';
}

function updateConfig(configString, command, key, newValue) {
  const config = parseConfig(configString);
  let updated = false;
  const updatedConfig = config.map((item) => {
    if (item.key === key && item.command === command) {
      updated = true;
      if (newValue === null || (command === '--put' && newValue === '')) {
        return null;
      }
      return { ...item, value: newValue };
    }
    return item;
  });

  if (!updated && newValue !== null && !(command === '--put' && newValue === '')) {
    updatedConfig.push({ command, key, value: newValue });
  }

  return updatedConfig
    .filter((item) => Boolean(item))
    .map((item) => `${item.command} ${item.key} ${item.value}`)
    .join(' ');
}

function isValidSerial(serial) {
  const regex = /^10\.0\.\d{1,3}\.\d{1,3}$/;
  if (regex.test(serial)) {
    const octets = serial.split('.');
    return octets.every((octet) => {
      const num = parseInt(octet, 10);
      return num >= 0 && num <= 255;
    });
  }
  return false;
}

function isBoxDownloading(box) {
  const { hasPdeProgress, totalBytes, downloadedBytes } = getBoxDownloadProgress(box);
  return box.contentSyncStatus === 'syncing' && (hasPdeProgress ? downloadedBytes < totalBytes : totalBytes > 0);
}

function getBoxDownloadProgress(box = {}) {
  const pdeTotalBytes = Number(box.totalContentSize);
  const pdeDownloadedBytes = Number(box.totalDownloadedContentSize);
  const parsedLegacyTotalBytes = Number(box.bytesToDownloadInCurrentIteration);
  const parsedLegacyDownloadedBytes = Number(box.bytesDownloaded);
  const legacyTotalBytes = Number.isFinite(parsedLegacyTotalBytes) ? parsedLegacyTotalBytes : 0;
  const legacyDownloadedBytes = Number.isFinite(parsedLegacyDownloadedBytes) ? parsedLegacyDownloadedBytes : 0;
  const hasPdeProgress = Number.isFinite(pdeTotalBytes) && pdeTotalBytes > 0 && Number.isFinite(pdeDownloadedBytes);
  const totalBytes = hasPdeProgress ? pdeTotalBytes : legacyTotalBytes;
  const downloadedBytes = hasPdeProgress ? pdeDownloadedBytes : legacyDownloadedBytes;
  const percent = totalBytes ? Math.min(100, (100 * downloadedBytes) / totalBytes) : 0;

  return {
    hasPdeProgress,
    totalBytes,
    downloadedBytes,
    percent,
  };
}

function formatBytesAsGb(bytes) {
  return (bytes / 1024 ** 3).toFixed(2);
}

function humanizeInstalledContentSize(bytes) {
  const parsed = Number(bytes);
  if (!Number.isFinite(parsed) || parsed < 1) {
    return '0';
  }
  return `${formatBytesAsGb(parsed)} GB`;
}

function getSyncCardMetrics({
  contentSyncStatus,
  contentSyncStatusPayload,
  activePendingContent = [],
  futurePendingContent = [],
  downloadSpeedBytesPerSec,
}) {
  const payload = contentSyncStatusPayload || {
    totalContentSize: 0,
    totalDownloadedContentSize: 0,
    totalActiveContentCount: 0,
    totalFutureContentCount: 0,
    totalDownloadedActiveContentCount: 0,
    totalDownloadedFutureContentCount: 0,
    totalInvalidContentCount: 0,
    totalOrphanedContentCount: 0,
  };
  const payloadTotalBytes = Number(payload.totalContentSize);
  const payloadDownloadedBytes = Number(payload.totalDownloadedContentSize);
  const hasPayloadByteProgress =
    Number.isFinite(payloadTotalBytes) && payloadTotalBytes > 0 && Number.isFinite(payloadDownloadedBytes);
  const syncTotalBytes = hasPayloadByteProgress ? payloadTotalBytes : 0;
  const syncDownloadedBytes = hasPayloadByteProgress ? payloadDownloadedBytes : 0;
  const syncTotalGb = formatBytesAsGb(syncTotalBytes);
  const syncDownloadedGb = formatBytesAsGb(syncDownloadedBytes);
  const syncProgressPercent = syncTotalBytes ? Math.min(100, (100 * syncDownloadedBytes) / syncTotalBytes) : 0;
  const syncPercent = Math.floor(syncProgressPercent);
  const isSyncingWithProgress =
    contentSyncStatus === 'syncing' && hasPayloadByteProgress && syncDownloadedBytes < syncTotalBytes;
  const payloadActiveCount = Number(payload.totalActiveContentCount);
  const payloadFutureCount = Number(payload.totalFutureContentCount);
  const payloadDownloadedActiveCount = Number(payload.totalDownloadedActiveContentCount);
  const payloadDownloadedFutureCount = Number(payload.totalDownloadedFutureContentCount);
  const payloadInvalidCount = Number(payload.totalInvalidContentCount);
  const payloadOrphanCount = Number(payload.totalOrphanedContentCount);
  const hasPayloadActiveTotals =
    Number.isFinite(payloadDownloadedActiveCount) && Number.isFinite(payloadActiveCount) && payloadActiveCount >= 0;
  const hasPayloadFutureTotals =
    Number.isFinite(payloadDownloadedFutureCount) && Number.isFinite(payloadFutureCount) && payloadFutureCount >= 0;
  const hasPayloadInvalidCount =
    contentSyncStatusPayload != null &&
    payload.totalInvalidContentCount != null &&
    Number.isFinite(payloadInvalidCount) &&
    payloadInvalidCount >= 0;
  const hasPayloadOrphanCount =
    contentSyncStatusPayload != null &&
    payload.totalOrphanedContentCount != null &&
    Number.isFinite(payloadOrphanCount) &&
    payloadOrphanCount >= 0;
  const contentProgressStringActive = hasPayloadActiveTotals
    ? `${payloadDownloadedActiveCount}/${payloadActiveCount}`
    : `${activePendingContent.filter((c) => c.status === 'ready').length}/${activePendingContent.length}`;
  const contentProgressStringFuture = hasPayloadFutureTotals
    ? `${payloadDownloadedFutureCount}/${payloadFutureCount}`
    : `${futurePendingContent.filter((c) => c.status === 'ready').length}/${futurePendingContent.length}`;
  const downloadSpeedHumanized =
    isSyncingWithProgress && downloadSpeedBytesPerSec ? humanizeDownloadSpeed(downloadSpeedBytesPerSec) : null;

  return {
    hasPayloadByteProgress,
    syncTotalBytes,
    syncDownloadedBytes,
    syncTotalGb,
    syncDownloadedGb,
    syncPercent,
    isSyncingWithProgress,
    contentProgressStringActive,
    contentProgressStringFuture,
    downloadSpeedHumanized,
    invalidContentCount: hasPayloadInvalidCount && payloadInvalidCount > 0 ? payloadInvalidCount : null,
    orphanedContentCount: hasPayloadOrphanCount && payloadOrphanCount > 0 ? payloadOrphanCount : null,
  };
}

function humanFormat(num, round = false) {
  let magnitude = 0;
  const suffixes = ['', 'k', 'M', 'G', 'T', 'P'];

  while (Math.abs(num) >= 1000 && magnitude < suffixes.length - 1) {
    magnitude += 1;
    num /= 1000.0;
  }

  let formattedNumber;
  if (round) {
    formattedNumber = Math.round(num); // Always return a whole number
  } else {
    formattedNumber = num % 1 === 0 ? num : num.toFixed(1); // Default behavior
  }

  return `${formattedNumber}${suffixes[magnitude]}`;
}

const formatNumberWithCommas = (number) => {
  if (!number) {
    return number;
  }
  return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

async function sleep(ms) {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

function normalizePathForPageVisits(originalPath = '') {
  let derivedPath;
  const databasesPrefix = '#databases';
  const databaseDocsPrefix = '#databaseDocs';
  const databaseDocPrefix = '#databaseDoc';

  if (originalPath.startsWith(databaseDocsPrefix)) {
    // Special handling for #databaseDocs path
    const hashPath = databaseDocsPrefix;
    const queryString = originalPath.split('?')[1] || '';
    const params = new URLSearchParams(queryString);
    const accountId = params.get('accountId');
    const dbName = params.get('dbName');

    const requiredParams = new URLSearchParams();
    if (accountId !== null) requiredParams.append('accountId', accountId);
    if (dbName !== null) requiredParams.append('dbName', dbName);

    // Ensure deterministic order
    const sortedRequiredParams = new URLSearchParams();
    Array.from(requiredParams.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .forEach(([key, value]) => sortedRequiredParams.append(key, value));

    const searchString = Array.from(sortedRequiredParams.entries())
      .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
      .join('&');
    derivedPath = hashPath + (searchString ? `?${searchString}` : '');
  } else if (originalPath.startsWith(databaseDocPrefix)) {
    // Special handling for #databaseDoc-details path
    const hashPath = databaseDocPrefix;
    const queryString = originalPath.split('?')[1] || '';
    const params = new URLSearchParams(queryString);
    const accountId = params.get('accountId');
    const dbName = params.get('dbName');
    const docId = params.get('docId');

    const requiredParams = new URLSearchParams();
    if (accountId !== null) requiredParams.append('accountId', accountId);
    if (dbName !== null) requiredParams.append('dbName', dbName);
    if (docId !== null) requiredParams.append('docId', docId);

    // Ensure deterministic order
    const sortedRequiredParams = new URLSearchParams();
    Array.from(requiredParams.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .forEach(([key, value]) => sortedRequiredParams.append(key, value));

    const searchString = Array.from(sortedRequiredParams.entries())
      .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
      .join('&');
    derivedPath = hashPath + (searchString ? `?${searchString}` : '');
  } else if (originalPath.startsWith(databasesPrefix)) {
    // Special handling for #databases path
    const hashPath = databasesPrefix;
    const queryString = originalPath.split('?')[1] || '';
    const params = new URLSearchParams(queryString);
    const accountId = params.get('accountId');
    const requiredParams = new URLSearchParams();
    if (accountId !== null) requiredParams.append('accountId', accountId);
    // Ensure deterministic order
    const sortedRequiredParams = new URLSearchParams();
    Array.from(requiredParams.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .forEach(([key, value]) => sortedRequiredParams.append(key, value));

    const searchString = Array.from(sortedRequiredParams.entries())
      .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
      .join('&');
    derivedPath = hashPath + (searchString ? `?${searchString}` : '');
  } else {
    // Existing logic for other paths
    // Need a base URL for the URL constructor, even if the path is relative/hash-based
    const dummyBase = 'http://dummy.com';
    // Handle potential errors if originalPath is not a valid URL/path segment
    try {
      const url = new URL(originalPath, dummyBase);
      url.searchParams.delete('page');
      url.searchParams.delete('pageSize');

      const sortedParams = new URLSearchParams();
      Array.from(url.searchParams.entries())
        .sort((a, b) => a[0].localeCompare(b[0]))
        .forEach(([key, value]) => sortedParams.append(key, value));

      const searchString = sortedParams.toString();
      // Reconstruct path: use pathname if it's not the dummy root, otherwise assume it was hash-based
      const pathPart = url.pathname && url.pathname !== '/' ? url.pathname : '';
      const searchPart = searchString ? `?${searchString}` : '';
      derivedPath = pathPart + searchPart + url.hash;
      // If the original path was only a hash, derivedPath might be empty, correct it
      if (!derivedPath && originalPath.startsWith('#')) {
        derivedPath = originalPath.split('?')[0] + searchPart;
      } else if (!derivedPath) {
        // Fallback if parsing somehow failed - return original minus page/pageSize crudely
        // This is less robust but prevents empty strings for unexpected inputs
        derivedPath = originalPath
          .replace(/([?&])page=\d+&?/, '$1')
          .replace(/([?&])pageSize=\d+&?/, '$1')
          .replace(/[?&]$/, '');
      }
    } catch (e) {
      console.error('Error parsing path in normalizePathForPageVisits:', originalPath, e);
      // Fallback to original path if URL parsing fails
      derivedPath = originalPath;
    }
  }
  return derivedPath;
}

function getBoxClockSkew(lastSeenTimestamp, timeDifferenceMs) {
  if (!timeDifferenceMs) return null;

  const formatTimeDifference = (ms) => {
    const absMs = Math.abs(ms);
    if (absMs < 1000) {
      return `${ms} ms`;
    }
    if (absMs < 60000) {
      return `${(ms / 1000).toFixed(1)} s`;
    }
    return `${(ms / 60000).toFixed(1)} min`;
  };

  const isWarning = Math.abs(timeDifferenceMs) > CLOCK_SKEW_THRESHOLD;
  const formattedTime = formatTimeDifference(timeDifferenceMs);

  if (isWarning) {
    return React.createElement(
      'span',
      {
        title: moment(lastSeenTimestamp).add(timeDifferenceMs, 'milliseconds').toISOString(),
      },
      React.createElement(
        'span',
        {
          style: { color: 'rgb(233, 30, 30)' },
        },
        React.createElement('i', { className: 'ri-error-warning-line' }),
        ` ${formattedTime}`,
      ),
    );
  }

  return React.createElement(
    'span',
    {
      title: moment(lastSeenTimestamp).add(timeDifferenceMs, 'milliseconds').toISOString(),
    },
    formattedTime,
  );
}

module.exports = {
  sortObjectKeys,
  humanizeFileSize,
  dehumanizeFileSize,
  humanizeDownloadSpeed,
  getQueryUrl,
  getAirfiCommandFromObject,
  getFirmwareFilename,
  parseFirmwareFilename,
  isFreeDiskSpaceOk,
  isFaseFirmwareOk,
  isFaseOnlineOk,
  isFaseContentOk,
  isFaseFutureContentOk,
  isWithinLastHours,
  isFaseRecentFlightsOk,
  buildQueryString,
  parseQueryString,
  groupBy,
  humanizePercentage,
  getDeviceByPosition,
  sortByPosition,
  hasAllKeys,
  getNameFromS3Key,
  parseISOSerial,
  humanizeError,
  isDeviceOnline,
  isWithinLastMinutes,
  getReadyStatus,
  isWithinLastDays,
  checkRole,
  getFilterLink,
  removeExtension,
  getBoxName,
  encodeBase64Json,
  decodeBase64Json,
  sanitizeRawFilterInput,
  extractValuesFromRawInput,
  extractSerialsFromRawInput,
  expandSerialFiltersForQuery,
  predictableStringify,
  stripDisplayContent,
  filterOnEnter,
  getHighlightedConfig,
  getManifestUrl,
  resolveManifestLinkDb,
  sanitizeManifestSourceDb,
  getManifestDbCandidates,
  getManifestAccountId,
  isRetailMasterDb,
  filterBoxEvents,
  exportCSVFile,
  highlightSubstring,
  validateAndFormatSerial,
  getConfig,
  updateConfig,
  stripArrays,
  isValidSerial,
  isBoxDownloading,
  getBoxDownloadProgress,
  formatBytesAsGb,
  humanizeInstalledContentSize,
  getSyncCardMetrics,
  humanFormat,
  formatNumberWithCommas,
  humanizeDurationInSecondsToHours,
  normalizePathForPageVisits,
  sleep,
  isExemptFromRoleCheck,
  getBoxClockSkew,
};
